import { useEffect, useState } from "react";
import "./Dashboard.css";
import { Device } from "../../helpers/types";
import JSZip from "jszip";
import { useDevices } from "../../context/devicesContext";
import GrafanaLineChart from "../../components/GrafanaLineChart";
import { fetchGrafanaData, GroupedData } from "../../services/chartService";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import GPSMap, { GPSPoint } from "../../components/GPSMap";
import ExportModal from "../../components/Modal";
import TimeRangePicker from "../../components/TimePicker";
import { dateRangeAtom } from "../../atoms/dataRangeAtom";
import { useAtom } from "jotai";

export default function DashboardPanel() {
  const [dateRange, setDateRange] = useAtom(dateRangeAtom);
  const { devices, isLoading: devicesLoading } = useDevices();
  const [currDevice, setCurrDevice] = useState<Device | null>(null);
  const [data, setData] = useState<GroupedData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  function handleDeviceOnClick(device: Device) {
    setCurrDevice(device);
  }

  const handleCheckboxChange = (id: string | number | undefined | null) => {
    if (id)
      setSelected((prev) => {
        const newSet = new Set(prev);
        newSet.has(id) ? newSet.delete(id) : newSet.add(id);
        return newSet;
      });
  };

  async function handleDownload() {
    const selectedData = data.filter(
      (obj) => obj.template && selected.has(obj.template.id)
    );

    if (selectedData.length === 0) {
      alert("No templates selected for export.");
      return;
    }

    const zipFile = new JSZip();

    selectedData.forEach(async (obj) => {
      const { template, data } = obj;

      if (template) {
        if ([1, 2, 3, 4, 5, 6].includes(template.id)) {
          let csv = "Time,Value\n";
          for (let i = 0; i < data.time.length; i++) {
            const time = String(data.time[i]).replace(/\n/g, " ");
            const value =
              data.simple_value[i] !== undefined &&
              data.simple_value[i] !== null
                ? data.simple_value[i]
                : "";
            csv += `${time},${value}\n`;
          }

          zipFile.file(`${template.title}.csv`, csv);
        }

        if ([7, 8, 9].includes(template.id)) {
          const chartData = data.time.map((t, i) => {
            let parsed: any = {};
            try {
              parsed = JSON.parse(data.complex_value[i] ?? "{}");
            } catch (err) {
              console.error("Invalid complex_value JSON", err);
            }
            return {
              time: t,
              curr: parsed.curr ?? {},
              volt: parsed.volt ?? {},
              freq: parsed.freq ?? {},
            };
          });

          const allowedKeys = {
            curr: ["IA", "IB", "IC", "IN"],
            volt: ["UA", "UB", "UC"],
            freq: ["A", "B", "C"],
          };

          const filterData = (data: any[], variant: "curr" | "volt" | "freq") =>
            data.map((item) => {
              const filtered: Record<string, any> = {
                time: item.time,
              };
              allowedKeys[variant].forEach((key) => {
                if (item[variant][key] !== undefined)
                  filtered[key] = item[variant][key];
              });
              return filtered;
            });

          const currData = filterData(chartData, "curr");
          const voltData = filterData(chartData, "volt");
          const freqData = filterData(chartData, "freq");

          if (template.name === "me440_curr") {
            let csv = "Time,IA,IB,IC,IN\n";
            for (let i = 0; i < currData.length; i++) {
              const time = String(currData[i].time).replace(/\n/g, " ");
              const IA = currData[i].IA;
              const IB = currData[i].IB;
              const IC = currData[i].IC;
              const IN = currData[i].IN;
              csv += `${time},${IA},${IB},${IC},${IN}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }

          if (template.name === "me440_volt") {
            let csv = "Time,UA,UB,UC\n";
            for (let i = 0; i < voltData.length; i++) {
              const time = String(voltData[i].time).replace(/\n/g, " ");
              const UA = voltData[i].UA;
              const UB = voltData[i].UB;
              const UC = voltData[i].UC;
              csv += `${time},${UA},${UB},${UC}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }

          if (template.name === "me440_curr") {
            let csv = "Time,A,B,C\n";
            for (let i = 0; i < freqData.length; i++) {
              const time = String(freqData[i].time).replace(/\n/g, " ");
              const A = freqData[i].A;
              const B = freqData[i].B;
              const C = freqData[i].A;
              csv += `${time},${A},${B},${C}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }
        }

        if ([10, 11, 12, 13, 14, 15].includes(template.id)) {
          const chartData = data.time.map((t, i) => {
            let parsed: any = {};
            try {
              parsed = JSON.parse(data.complex_value[i] ?? "{}");
            } catch (err) {
              console.error("Invalid complex_value JSON", err);
            }

            return {
              time: t,
              speed: parsed.speed ?? null,
              altitude: parsed.altitude ?? null,
              satellites: parsed.satellites ?? null,
              latitude: parsed.latitude ?? null,
              longitude: parsed.longitude ?? null,
            };
          });

          if (template.id === 10) {
            let csv = "Time,speed,altitude,satellites,latitude,longitude\n";
            for (let i = 0; i < chartData.length; i++) {
              const time = String(chartData[i].time).replace(/\n/g, " ");
              const speed = chartData[i].speed;
              const altitude = chartData[i].altitude;
              const satellites = chartData[i].satellites;
              const latitude = chartData[i].latitude;
              const longitude = chartData[i].longitude;
              csv += `${time},${speed},${altitude},${satellites},${latitude},${longitude}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }

          if (template.id === 11) {
            let csv = "Time,speed\n";
            for (let i = 0; i < chartData.length; i++) {
              const time = String(chartData[i].time).replace(/\n/g, " ");
              const speed = chartData[i].speed;
              csv += `${time},${speed}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }

          if (template.id === 12) {
            let csv = "Time,altitude\n";
            for (let i = 0; i < chartData.length; i++) {
              const time = String(chartData[i].time).replace(/\n/g, " ");
              const altitude = chartData[i].altitude;
              csv += `${time},${altitude}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }

          if (template.id === 13) {
            let csv = "Time,satellites\n";
            for (let i = 0; i < chartData.length; i++) {
              const time = String(chartData[i].time).replace(/\n/g, " ");
              const satellites = chartData[i].satellites;
              csv += `${time},${satellites}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }

          if (template.id === 14) {
            let csv = "Altitude,speed\n";
            for (let i = 0; i < chartData.length; i++) {
              const altitude = chartData[i].altitude;
              const speed = chartData[i].speed;
              csv += `${altitude},${speed}\n`;
            }

            zipFile.file(`${template.title}.csv`, csv);
          }

          if (template.id === 15) {
            let csv = "Time,latitude difference,longitude difference\n";

            chartData.forEach((item, i) => {
              const time = String(chartData[i].time).replace(/\n/g, " ");

              if (i === 0) {
                csv += `${time},0,0\n`;
              } else {
                csv += `${time},${
                  (item.latitude ?? 0) - (chartData[i - 1].latitude ?? 0)
                },${
                  (item.longitude ?? 0) - (chartData[i - 1].longitude ?? 0)
                }\n`;
              }
            });

            zipFile.file(`${template.title}.csv`, csv);
          }
        }

        if ([16, 17, 18, 19].includes(template.id)) {
          const codeMap = {
            "239": "Ignition",
            "240": "Movement",
            "800": "Extended external voltage",
            "841": "Overcurrent",
          } as const;

          type CodeKey = keyof typeof codeMap;

          const chartData = data.time.map(
            (t: string | number | null, i: number) => {
              let parsed: Record<string, number> = {};
              try {
                parsed = JSON.parse(data.complex_value[i] ?? "{}");
              } catch (err) {
                console.error("Invalid complex_value JSON", err);
              }

              return {
                time: t,
                ...parsed,
              };
            }
          );

          const prepareChartData = (code: CodeKey) =>
            chartData.forEach((item) => {
              const parsedItem = item as Record<string, number | string | null>;
              const result = {
                time: parsedItem.time,
                value: (parsedItem[code] as number) ?? 0
              }

              csv += `${result.time},${result.value}\n`;
            });

          const variant = template?.name ?? "";
          let csv: string = "";

          if (variant === "2_ign") {
            csv += "Time,Ignition\n";
            prepareChartData("239");
            zipFile.file(`${template.title}.csv`, csv);
          }

          if (variant === "2_move") {
            csv += "Time,Movement\n";
            prepareChartData("240");
            zipFile.file(`${template.title}.csv`, csv);
          }

          if (variant === "2_volt") {
            csv += "Time,Voltage\n";
            prepareChartData("800");
            zipFile.file(`${template.title}.csv`, csv);
          }

          if (variant === "2_overcurr") {
            csv += "Time,Overcurrent\n";
            prepareChartData("841");
            zipFile.file(`${template.title}.csv`, csv);
          }
        }
      }
    });

    // generate and download the ZIP
    const blob = await zipFile.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "exported_templates.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setModalOpen(false);
  }

  useEffect(() => {
    if (!currDevice && devices && devices.length > 0) {
      setCurrDevice(devices[0]);
    }
  }, [devices, currDevice]);

  useEffect(() => {
    const loadData = async () => {
      if (!currDevice) return;

      setLoading(true);
      setError("");

      try {
        const chartData = await fetchGrafanaData(currDevice.name, dateRange.from, dateRange.to);
        const chartDataWithTemplates = chartData.flatMap((chart) => {
          const matchingTemplates = currDevice.templates.filter(
            (t) => t.name === chart.code || t.name.startsWith(`${chart.code}_`)
          );

          if (matchingTemplates.length > 0) {
            return matchingTemplates.map((template) => ({
              ...chart,
              key: `${chart.code}-${chart.template?.name}`,
              template,
            }));
          }

          return [{ ...chart, template: null }];
        });

        setData(chartDataWithTemplates);
      } catch (err: any) {
        setError(err.message || "Failed to load chart data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currDevice, dateRange]);

  return (
    <div className="dashboard-container">
      <ExportModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onExport={handleDownload}
        title="Data Export"
      >
        <p className="text-gray-700">
          Do you want to export the selected data set as a CSV file?
        </p>

        {data.length === 0 ? (
          <p>No templates available for export.</p>
        ) : (
          <div className="template-grid">
            {data.map((d) => {
              if (d.template) {
                return (
                  <label key={d.template.id} className="template-item">
                    <input
                      type="checkbox"
                      checked={selected.has(d.template.id)}
                      onChange={() => handleCheckboxChange(d.template?.id)}
                    />
                    <span>{d.template.title}</span>
                  </label>
                );
              } else return null;
            })}
          </div>
        )}
      </ExportModal>

      <div className="dashboard-panel">
        <div className="dashboard-sidebar">
          <h3 className="dashboard-sidebar-title">Devices</h3>

          {devicesLoading && <p>Loading devices...</p>}
          {!devicesLoading && (!devices || devices.length === 0) && (
            <p>No devices found.</p>
          )}

          {devices && devices.length > 0 && (
            <div className="devices-list">
              {devices.map((device, i) => (
                <div
                  key={i}
                  className={`device ${
                    currDevice?.name === device.name ? "active" : ""
                  }`}
                  onClick={() => handleDeviceOnClick(device)}
                >
                  <div className="device-data">
                    {device.alias ? (
                      <div className="device-alias">{device.alias}</div>
                    ) : null}
                    <div className="device-name">{device.name}</div>
                    <div className="device-type">{device.device_type}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-content">
          <div className="dashboard-title">
            <h2>Dashboard content</h2>
            {currDevice !== null ? (
              <div
                className="download-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setModalOpen(true);
                }}
              >
                <svg
                  width={28}
                  height={28}
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.29289 1.29289C9.48043 1.10536 9.73478 1 10 1H18C19.6569 1 21 2.34315 21 4V17C21 17.5523 20.5523 18 20 18C19.4477 18 19 17.5523 19 17V4C19 3.44772 18.5523 3 18 3H14.5H11V8C11 8.55228 10.5523 9 10 9H5V20C5 20.5523 5.44772 21 6 21H8C8.55228 21 9 21.4477 9 22C9 22.5523 8.55228 23 8 23H6C4.34315 23 3 21.6569 3 20V8C3 7.73478 3.10536 7.48043 3.29289 7.29289L9.29289 1.29289ZM14.5 3C15.3284 3 16 3.67157 16 4.5C16 5.32843 15.3284 6 14.5 6C13.6716 6 13 5.32843 13 4.5C13 3.67157 13.6716 3 14.5 3ZM6.41421 7H9V4.41421L6.41421 7ZM11 20.577C11 22.4675 12.5325 24 14.423 24H14.577C16.4675 24 18 22.4675 18 20.577C18 19.8707 17.82 19.176 17.4769 18.5585L15.9856 15.8742C15.6859 15.3346 15.1172 15 14.5 15C13.8828 15 13.3141 15.3346 13.0144 15.8742L11.5231 18.5585C11.18 19.176 11 19.8707 11 20.577ZM14.5 22C15.3284 22 16 21.3284 16 20.5C16 19.6716 15.3284 19 14.5 19C13.6716 19 13 19.6716 13 20.5C13 21.3284 13.6716 22 14.5 22ZM14.5 14C15.3284 14 16 13.3284 16 12.5C16 11.6716 15.3284 11 14.5 11C13.6716 11 13 11.6716 13 12.5C13 13.3284 13.6716 14 14.5 14ZM16 8.5C16 9.32843 15.3284 10 14.5 10C13.6716 10 13 9.32843 13 8.5C13 7.67157 13.6716 7 14.5 7C15.3284 7 16 7.67157 16 8.5Z"
                    fill="#334155"
                  />
                </svg>
              </div>
            ) : null}
          </div>
          <TimeRangePicker
            defaultRangeKey="last-6h"
            onChange={({ from, to }) => setDateRange({ from, to })}
          />
          {devicesLoading && <p>Loading devices...</p>}
          {!devicesLoading && devices?.length === 0 && <p>No devices found.</p>}
          {!devicesLoading && devices && devices?.length > 0 && !currDevice && (
            <p>Please select a device</p>
          )}

          {currDevice && (
            <>
              {loading && <p>Loading chart for {currDevice.name}...</p>}
              {error && <p className="error">Error: {error}</p>}

              {!loading && !error && data.length > 0 && (
                <div className="dive">
                  {data.map((group) => {
                    if (group.template) {
                      if (
                        group.template.name === "meter1" ||
                        group.template.name === "meter2" ||
                        group.template.name === "meter3" ||
                        group.template.name === "i2c_adc" ||
                        group.template.name === "i2c_humidity" ||
                        group.template.name === "i2c_temp"
                      ) {
                        const chartData = group.data.time.map((time, i) => ({
                          time: time ?? "",
                          value: group.data.simple_value[i] ?? 0,
                        }));

                        return (
                          <div
                            key={`${group.metering_point_id} ${group.template.name}`}
                          >
                            <h4>{group.template.title}</h4>
                            <GrafanaLineChart
                              data={
                                ["meter1", "meter2", "meter3"].includes(
                                  group.template.name
                                )
                                  ? chartData
                                  : chartData.reverse()
                              }
                              xAxis={group.template.xAxis}
                              yAxis={group.template.yAxis[0]}
                              domain={group.template.domain}
                              tick={group.template.tick}
                            />
                          </div>
                        );
                      }

                      if (group.template.name.startsWith("me440")) {
                        const chartData = group.data.time.map((t, i) => {
                          let parsed: any = {};
                          try {
                            parsed = JSON.parse(
                              group.data.complex_value[i] ?? "{}"
                            );
                          } catch (err) {
                            console.error("Invalid complex_value JSON", err);
                          }
                          return {
                            time: t,
                            curr: parsed.curr ?? {},
                            volt: parsed.volt ?? {},
                            freq: parsed.freq ?? {},
                          };
                        });

                        const allowedKeys = {
                          curr: ["IA", "IB", "IC", "IN"],
                          volt: ["UA", "UB", "UC"],
                          freq: ["A", "B", "C"],
                        };

                        const filterData = (
                          data: any[],
                          variant: "curr" | "volt" | "freq"
                        ) =>
                          data.map((item) => {
                            const filtered: Record<string, any> = {
                              time: item.time,
                            };
                            allowedKeys[variant].forEach((key) => {
                              if (item[variant][key] !== undefined)
                                filtered[key] = item[variant][key];
                            });
                            return filtered;
                          });

                        const currData = filterData(chartData, "curr");
                        const voltData = filterData(chartData, "volt");
                        const freqData = filterData(chartData, "freq");

                        const renderTick = (props: any) => {
                          const { x, y, payload } = props;
                          const value =
                            payload?.value != null ? String(payload.value) : "";
                          const lines = value.split("\n");
                          const offset = 8;

                          return (
                            <text
                              x={x}
                              y={y + offset}
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {lines.map((line: string, index: number) => (
                                <tspan
                                  key={index}
                                  x={x}
                                  dy={index === 0 ? 0 : 15}
                                >
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          );
                        };

                        if (group.template.name === "me440_curr") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${group.template.name}`}
                              className="chart-wrapper"
                            >
                              <h4>Current</h4>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={currData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="time" tick={renderTick} />
                                  <YAxis />
                                  <Tooltip />
                                  <Legend
                                    verticalAlign="top"
                                    align="right"
                                    height={36}
                                  />
                                  {allowedKeys.curr.map((key, idx) => (
                                    <Line
                                      key={key}
                                      type="monotone"
                                      dataKey={key}
                                      stroke={
                                        [
                                          "#4F46E5",
                                          "#EC4899",
                                          "#F59E0B",
                                          "#10B981",
                                        ][idx]
                                      }
                                      strokeWidth={2}
                                      dot={false}
                                    />
                                  ))}
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        }

                        if (group.template.name === "me440_volt") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${group.template.name}`}
                              className="chart-wrapper"
                            >
                              <h4>Voltage</h4>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={voltData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="time" tick={renderTick} />
                                  <YAxis
                                    domain={[
                                      (dataMin: number) =>
                                        Math.floor(dataMin - 1),
                                      (dataMax: number) =>
                                        Math.ceil(dataMax + 1),
                                    ]}
                                  />
                                  <Tooltip />
                                  <Legend
                                    verticalAlign="top"
                                    align="right"
                                    height={36}
                                  />
                                  {allowedKeys.volt.map((key, idx) => (
                                    <Line
                                      key={key}
                                      type="monotone"
                                      dataKey={key}
                                      stroke={
                                        ["#4F46E5", "#EC4899", "#F59E0B"][idx]
                                      }
                                      strokeWidth={2}
                                      dot={false}
                                    />
                                  ))}
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        }

                        if (group.template.name === "me440_freq") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${group.template.name}`}
                              className="chart-wrapper"
                            >
                              <h4>Frequency</h4>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={freqData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="time" tick={renderTick} />
                                  <YAxis
                                    domain={[
                                      (dataMin: number) => Math.floor(dataMin),
                                      (dataMax: number) => Math.ceil(dataMax),
                                    ]}
                                  />
                                  <Tooltip />
                                  <Legend
                                    verticalAlign="top"
                                    align="right"
                                    height={36}
                                  />
                                  {allowedKeys.freq.map((key, idx) => (
                                    <Line
                                      key={key}
                                      type="monotone"
                                      dataKey={key}
                                      stroke={
                                        ["#4F46E5", "#EC4899", "#F59E0B"][idx]
                                      }
                                      strokeWidth={2}
                                      dot={false}
                                    />
                                  ))}
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        }
                      }

                      if (group.template.name.startsWith("1")) {
                        const chartData = group.data.time.map((t, i) => {
                          let parsed: any = {};
                          try {
                            parsed = JSON.parse(
                              group.data.complex_value[i] ?? "{}"
                            );
                          } catch (err) {
                            console.error("Invalid complex_value JSON", err);
                          }

                          return {
                            time: t,
                            speed: parsed.speed ?? null,
                            altitude: parsed.altitude ?? null,
                            satellites: parsed.satellites ?? null,
                            latitude: parsed.latitude ?? null,
                            longitude: parsed.longitude ?? null,
                          };
                        });

                        const prepareChartData = (
                          key: "speed" | "altitude" | "satellites"
                        ) =>
                          chartData.map((item) => ({
                            time: item.time,
                            value: item[key],
                          }));

                        const altitideSpeedData = chartData.map((item) => ({
                          altitude: item.altitude,
                          speed: item.speed,
                        }));

                        const diffData = chartData.map((item, i) => {
                          if (i === 0) {
                            return {
                              time: item.time,
                              latitudeDiff: 0,
                              longitudeDiff: 0,
                            };
                          }

                          return {
                            time: item.time,
                            latitudeDiff:
                              (item.latitude ?? 0) -
                              (chartData[i - 1].latitude ?? 0),
                            longitudeDiff:
                              (item.longitude ?? 0) -
                              (chartData[i - 1].longitude ?? 0),
                          };
                        });

                        const gpsMapData: GPSPoint[] = chartData
                          .filter(
                            (d) =>
                              d.latitude !== null &&
                              d.longitude !== null &&
                              d.time !== null
                          )
                          .map((d) => ({
                            time: String(d.time),
                            latitude: d.latitude as number,
                            longitude: d.longitude as number,
                            speed: d.speed ?? 0,
                          }));

                        const renderTick = (props: any) => {
                          const { x, y, payload } = props;
                          const value =
                            payload?.value != null ? String(payload.value) : "";
                          const lines = value.split("\n");
                          const offset = 8;

                          return (
                            <text
                              x={x}
                              y={y + offset}
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {lines.map((line: string, index: number) => (
                                <tspan
                                  key={index}
                                  x={x}
                                  dy={index === 0 ? 0 : 15}
                                >
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          );
                        };

                        const variant = group.template?.name ?? "";

                        if (variant === "1") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${variant}`}
                              className="chart-wrapper"
                            >
                              <h4>Map</h4>
                              <GPSMap data={gpsMapData} />
                            </div>
                          );
                        }

                        if (variant === "1_speed") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${variant}`}
                              className="chart-wrapper"
                            >
                              <h4>Speed</h4>
                              <GrafanaLineChart
                                data={prepareChartData("speed")}
                                xAxis="time"
                                yAxis="value"
                                domain={false}
                                tick={true}
                              />
                            </div>
                          );
                        }

                        if (variant === "1_alt") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${variant}`}
                              className="chart-wrapper"
                            >
                              <h4>Altitude</h4>
                              <GrafanaLineChart
                                data={prepareChartData("altitude")}
                                xAxis="time"
                                yAxis="value"
                                domain={false}
                                tick={true}
                              />
                            </div>
                          );
                        }

                        if (variant === "1_sat") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${variant}`}
                              className="chart-wrapper"
                            >
                              <h4>Satellites</h4>
                              <GrafanaLineChart
                                data={prepareChartData("satellites")}
                                xAxis="time"
                                yAxis="value"
                                domain={false}
                                tick={true}
                              />
                            </div>
                          );
                        }

                        if (variant === "1_alt_speed") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${variant}`}
                              className="chart-wrapper"
                            >
                              <h4>Altitude vs Speed</h4>
                              <GrafanaLineChart
                                data={altitideSpeedData}
                                xAxis="altitude"
                                yAxis="speed"
                                domain={false}
                                tick={false}
                              />
                            </div>
                          );
                        }

                        if (variant === "1_lat_lon_diff") {
                          return (
                            <div
                              key={`${group.metering_point_id}-${variant}`}
                              className="chart-wrapper"
                            >
                              <h4>Latitude / Longitude Differences</h4>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={diffData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="time" tick={renderTick} />
                                  <YAxis />
                                  <Tooltip />
                                  <Legend
                                    verticalAlign="top"
                                    align="right"
                                    height={36}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="latitudeDiff"
                                    stroke="#10B981"
                                    strokeWidth={2}
                                    dot={false}
                                    name="Latitude Δ"
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="longitudeDiff"
                                    stroke="#3B82F6"
                                    strokeWidth={2}
                                    dot={false}
                                    name="Longitude Δ"
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        }
                      }

                      if (group.template.name.startsWith("2")) {
                        const codeMap = {
                          "239": "Ignition",
                          "240": "Movement",
                          "800": "Extended external voltage",
                          "841": "Overcurrent",
                        } as const;

                        type CodeKey = keyof typeof codeMap;

                        const chartData = group.data.time.map(
                          (t: string | number | null, i: number) => {
                            let parsed: Record<string, number> = {};
                            try {
                              parsed = JSON.parse(
                                group.data.complex_value[i] ?? "{}"
                              );
                            } catch (err) {
                              console.error("Invalid complex_value JSON", err);
                            }

                            return {
                              time: t,
                              ...parsed,
                            };
                          }
                        );

                        const prepareChartData = (code: CodeKey) =>
                          chartData.map((item) => {
                            const parsedItem = item as Record<
                              string,
                              number | string | null
                            >;
                            return {
                              time: parsedItem.time,
                              value: (parsedItem[code] as number) ?? 0,
                            };
                          });

                        const variant = group.template?.name ?? "";

                        const renderChart = (code: CodeKey) => (
                          <div
                            key={`${group.metering_point_id}-${variant}`}
                            className="chart-wrapper"
                          >
                            <h4>{codeMap[code]}</h4>
                            <GrafanaLineChart
                              data={prepareChartData(code).reverse()}
                              xAxis="time"
                              yAxis="value"
                              domain={false}
                              tick={true}
                            />
                          </div>
                        );

                        if (variant === "2_ign") return renderChart("239");
                        if (variant === "2_move") return renderChart("240");
                        if (variant === "2_volt") return renderChart("800");
                        if (variant === "2_overcurr") return renderChart("841");
                      }
                    }

                    return null;
                  })}
                </div>
              )}

              {!loading && !error && data.length === 0 && (
                <p>No chart data available for {currDevice.name}.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
